/**
 * Design Tokens for AVA Platform Design System
 *
 * Centralized design tokens that define the visual language.
 * All components should reference these tokens instead of hardcoded values.
 */

export const colors = {
  primary: {
    50: '#DAEDFF',   // --color-ava-blue-08 (lightest)
    100: '#4277BB',  // --color-ava-blue-07
    200: '#2B65B0',  // --color-ava-blue-06
    300: '#114A8E',  // --color-ava-blue-05
    400: '#0C3C60',  // --color-ava-blue-04
    500: '#132B51',  // --color-ava-blue-03 (base)
    600: '#0C1629',  // --color-ava-blue-02
    700: '#071423',  // --color-ava-blue-01 (darkest)
  },

  accent: {
    500: '#CF1B5A',  // --color-ava-pink-02
    600: '#891D44',  // --color-ava-pink-01
  },

  neutral: {
    50: '#F3F5F8',   // --color-ava-grey-02 (lightest)
    100: '#F9FAFB',
    200: '#E5E7EB',
    300: '#D1D5DB',
    400: '#9CA3AF',
    500: '#6B7280',
    600: '#4B5563',
    700: '#374151',
    800: '#323232',  // --color-ava-grey-01
    900: '#1F2937',
    950: '#241E20',  // --color-ava-black (darkest)
  },

  // Background colors
  background: {
    page: '#F3F5F8',      // Default page background for admin pages
    hover: 'rgba(0, 0, 0, 0.02)',
  },

  // Semantic colors
  success: {
    50: '#F0F9FF',
    500: '#52C41A',
    600: '#389E0D',
  },
  warning: {
    50: '#FFFBEB',
    500: '#FAAD14',
    600: '#D48806',
  },
  error: {
    50: '#FEF2F2',
    500: '#FF4D4F',
    600: '#CF1322',
  },
  info: {
    50: '#EFF6FF',
    500: '#1890FF',
    600: '#0958D9',
  },
}

export const typography = {
  fontFamily: {
    body: 'Roboto, sans-serif',
    heading: 'Roboto, sans-serif',
  },

  // Numeric font sizes (for Ant Design theme config)
  fontSizeNum: {
    xs: 12,
    sm: 14,
    base: 16,
    lg: 18,
    xl: 20,
    '2xl': 24,
    '3xl': 30,
    '4xl': 36,
  },

  // String font sizes with units (for CSS usage)
  fontSize: {
    xs: '12px',
    sm: '14px',
    base: '16px',
    lg: '18px',
    xl: '20px',
    '2xl': '24px',
    '3xl': '30px',
    '4xl': '36px',
  },

  fontWeight: {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },

  lineHeight: {
    tight: 1.25,
    normal: 1.5,
    relaxed: 1.75,
  },
}

export const spacing = {
  // String spacing with rem units (for CSS usage)
  xxs: '0.125rem', // 2px
  xs: '0.25rem',   // 4px
  sm: '0.5rem',    // 8px
  md: '0.75rem',   // 12px
  lg: '1rem',      // 16px
  xl: '1.5rem',    // 24px
  '2xl': '2rem',   // 32px
  '3xl': '3rem',   // 48px
  '4xl': '4rem',   // 64px
}

// Numeric spacing values (for Ant Design theme config - accepts only numbers as px)
export const spacingNum = {
  xxs: 2,
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 24,
  '2xl': 32,
  '3xl': 48,
  '4xl': 64,
}

export const borderRadius = {
  none: '0px',
  sm: '2px',
  md: '4px',
  lg: '8px',
  full: '9999px',
}

export const shadows = {
  sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  base: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
  xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
}

export const breakpoints = {
  sm: '640px',
  md: '768px',
  lg: '1024px',
  xl: '1280px',
  '2xl': '1536px',
}

/**
 * Table column width tokens
 * Based on content type and typical data length
 * Max width uses 2xl breakpoint / 2.5 ≈ 614px for comfortable reading
 */
export const columnWidth = {
  // Numeric values for Ant Design Table (in pixels)
  xs: 80,      // Icons, short codes
  sm: 120,     // Dates, status badges
  md: 160,     // Phone numbers, short text
  lg: 200,     // Names, medium text
  xl: 280,     // Emails, longer text
  '2xl': 360,  // Descriptions, addresses
  max: 600,    // Maximum readable width (≈ breakpoints.sm)
}

/**
 * Table column width tokens as CSS strings
 */
export const columnWidthStr = {
  xs: '80px',
  sm: '120px',
  md: '160px',
  lg: '200px',
  xl: '280px',
  '2xl': '360px',
  max: '600px',
}
